/**
 * Roteiros do guia na tela, um por item do card Primeiros passos (mesma chave
 * devolvida por FirstSteps.php). Cada passo aponta para um elemento marcado com
 * data-tour="..." na tela; o guia mostra o passo mais adiantado cujo elemento
 * está visível, então abrir um formulário já avança o balão sozinho.
 *
 * dim: escurece o resto da tela e bloqueia os cliques fora do elemento destacado
 * (e dos listados em also). Desligado dentro de janelas e painéis, que já têm o
 * próprio fundo.
 *
 * highlight: outros elementos destacados junto com o alvo (os campos que a pessoa
 * preenche); o resto da tela fica travado, como no dim, mas sem escurecer.
 *
 * ready: condição extra para o passo valer, quando o elemento já aparece antes
 * da hora.
 *
 * confirm: rótulo de um botão no balão para itens que ficam feitos só de ver a
 * tela (estatísticas): grava a visita no servidor (FirstSteps::markSeen).
 *
 * intro: janela de apresentação no centro, já na tela do item, antes do primeiro
 * destaque: diz onde a pessoa está e o que vamos fazer; menu é o caminho da tela
 * no menu lateral (grupo e item, como em AppConfig), para voltar sozinha depois.
 *
 * done: janela de conclusão do item: comemora o que a pessoa acabou de fazer, antes
 * do "Agora vamos" com o próximo passo.
 *
 * video: item que é assistir a uma lição (cores da agenda): sem passos na tela,
 * o vídeo abre em janela logo depois da apresentação e a comemoração espera a
 * janela fechar.
 *
 * startHere: prefixos de rota onde o roteiro já começa sem sair da tela (o
 * pagamento começa na tela do atendimento que a pessoa acabou de registrar); em
 * qualquer outra tela o guia leva para path.
 *
 * savedWhenGone: no último passo, o salvamento só é conferido quando nenhum
 * passo sobra na tela (o Registrar do atendimento fecha a janela e some só
 * depois de gravar).
 *
 * showcase: roteiro de ver, não de fazer (EXAMPLE_TOUR, "Conheça sua conta").
 * Não é item dos Primeiros passos e nada é conferido no servidor: cada passo
 * tem a própria tela (path, ou uma função que recebe CompanyConfig.DemoData e
 * devolve a rota, ou null para pular o passo) e a pessoa avança pelo botão
 * Próximo (next troca o rótulo).
 * Opções de um passo do showcase:
 *   openTab: rótulo da aba (.category-tab) que o guia abre uma vez ao chegar
 *            (a ficha de exemplo abre na aba Saúde);
 *   target:  um data-tour ou uma lista deles (vale o primeiro que estiver na tela);
 *   textCompact: texto no lugar de text no celular (tela de até 600px: "Toque" em vez de "Clique");
 *                vale também nos passos dos Primeiros passos e em intro.textCompact;
 *   titleCompact: título no lugar de title no celular (só nos passos dos Primeiros passos);
 *   summary: em vez de destacar um alvo, mostra no centro o resumo simulado da
 *            semana com os exemplos (GuidedTourSummary, nada é enviado nem gravado).
 */

/** Guia que mostra a conta preenchida com os exemplos (docs/features/trial-demo-data, seção 3). */
export const EXAMPLE_TOUR = 'example';
const TOUR_SCRIPTS = {
  [EXAMPLE_TOUR]: {
    showcase: true,
    label: 'Conheça sua conta',
    intro: {
      title: 'Conheça sua conta',
      text: 'Em seis paradas rápidas, você vê a agenda, as pessoas, uma ficha completa, os números e o que a Gabriela adianta para você, tudo já preenchido com os exemplos da sua área. Depois é só explorar à vontade.',
    },
    steps: [
      {
        path: '/schedule/appointments',
        target: 'agenda-week',
        title: 'Sua agenda, já com uma rotina',
        text: 'São semanas de atendimentos de exemplo nos seus horários. A cor de cada um mostra em que pé está: marcado, confirmado, atendido ou falta. Clique em um quando quiser ver os detalhes.',
        textCompact: 'Este é o dia de hoje, com atendimentos de exemplo nos seus horários. A cor de cada um mostra em que pé está: marcado, confirmado, atendido ou falta. Toque em um para ver os detalhes, ou em Semana e Mês, no topo, para ver os outros dias.',
        dim: true,
      },
      {
        path: '/patients',
        target: 'patients-list',
        title: 'As pessoas que você atende',
        text: 'Cada uma tem ficha, histórico e contato. As de exemplo levam o selo Exemplo, para nunca se misturarem com as suas.',
        dim: true,
      },
      {
        path: (demoData) => (demoData?.featured_patient_id ? `/patients/${demoData.featured_patient_id}` : null),
        // A aba Saúde é a que mostra os registros da área (escalas, medidas, curva)
        target: ['patient-health', 'patient-record'],
        openTab: 'Saúde',
        title: 'Uma ficha completa',
        text: 'Na aba Saúde ficam as evoluções e os registros da sua área. É assim que cada ficha vai se formando a cada atendimento.',
        dim: true,
      },
      {
        path: '/statistics',
        target: 'statistics-overview',
        title: 'Os números do período',
        text: 'Atendimentos, faturamento e pessoas atendidas entram aqui sozinhos, sem planilha. Repare nos avisos: atendimento realizado sem pagamento registrado aparece aqui, para nada ficar para trás.',
        dim: true,
      },
      {
        path: '/dashboard',
        summary: true,
        title: 'O que a Gabriela fez por você',
        text: 'Uma semana como esta, com os exemplos. Simulação: nada foi enviado.',
        next: 'Continuar',
      },
      {
        path: '/dashboard',
        target: 'demo-banner',
        title: 'Agora é com você',
        text: 'Cadastre a primeira pessoa e marque o primeiro atendimento: a confirmação fica pronta na véspera. Os exemplos podem ficar; quando quiser, remova por esta faixa. Ao assinar, eles saem sozinhos.',
        dim: true,
        next: 'Começar a usar',
      },
    ],
  },
  patient: {
    path: '/patients',
    pathLabel: 'Pacientes',
    intro: {
      title: 'Cadastre sua primeira pessoa',
      menu: ['Geral', 'Pacientes'],
      text: 'Esta é a tela de Pacientes: aqui ficam todas as pessoas que você atende, com ficha, histórico e contato. Vamos cadastrar a primeira, só com nome e telefone.',
    },
    done: {
      title: 'Paciente cadastrado!',
      text: 'Viu como é fácil? A ficha já está pronta, e o histórico vai se formando a cada atendimento.',
    },
    steps: [
      {
        target: 'patients-new',
        title: 'Clique em Novo paciente',
        text: 'É por aqui que você cadastra quem atende.',
        dim: true,
      },
      {
        target: 'patient-form-add',
        title: 'Nome e telefone de contato já bastam',
        text: 'Preencha os dois e clique em Adicionar. O resto pode ficar para depois.',
        dim: false,
        highlight: ['patient-form-name', 'patient-form-ddi', 'patient-form-phone'],
      },
    ],
  },
  appointment: {
    path: '/schedule/appointments',
    pathLabel: 'Agenda',
    intro: {
      title: 'Agora, a agenda',
      menu: ['Agenda', 'Agendamentos'],
      text: 'Aqui você vê os dias da semana com os horários do seu expediente. Vamos marcar um atendimento: na véspera, a confirmação fica pronta para sair pelo WhatsApp.',
      textCompact: 'Aqui você vê o dia com os horários do seu expediente. Vamos marcar um atendimento: na véspera, a confirmação fica pronta para sair pelo WhatsApp.',
    },
    done: {
      title: 'Atendimento marcado!',
      text: 'Ele já está na agenda. Na véspera, a confirmação fica pronta no Início; com o seu número conectado, sai sozinha pelo WhatsApp.',
    },
    steps: [
      {
        target: 'agenda-create',
        title: 'Clique em Criar agendamento',
        titleCompact: 'Toque no +',
        text: 'Você escolhe o dia e o horário no passo seguinte.',
        textCompact: 'Fica no topo da agenda. Você escolhe o dia e o horário no passo seguinte.',
        dim: true,
      },
      {
        target: 'agenda-picker',
        title: 'Escolha o dia e o horário',
        text: 'Aparecem os horários livres do expediente que você configurou.',
        dim: false,
      },
      {
        target: 'agenda-slot-new',
        title: 'Clique em Novo agendamento',
        text: 'O horário que você clicou já vem preenchido.',
        dim: false,
      },
      {
        target: 'agenda-save',
        title: 'Escolha o paciente e salve',
        text: 'Busque o paciente pelo nome (ou cadastre ali mesmo), escolha o atendimento e clique em Salvar agendamento. Na véspera, a confirmação fica pronta para sair pelo WhatsApp.',
        dim: false,
      },
    ],
  },
  colors: {
    path: '/schedule/appointments',
    pathLabel: 'Agenda',
    video: true,
    intro: {
      title: 'As cores da agenda',
      menu: ['Agenda', 'Agendamentos'],
      text: 'Cada agendamento muda de cor conforme o status: marcado, confirmado, atendido, faltou. Um vídeo curto mostra o que cada uma quer dizer, para você ler a agenda de relance.',
    },
    done: {
      title: 'Agora você lê a agenda de relance!',
      text: 'A cor de cada agendamento mostra em que pé ele está, sem precisar abrir.',
    },
    steps: [],
  },
  attend: {
    path: '/schedule/appointments',
    pathLabel: 'Agenda',
    intro: {
      title: 'Hora de atender',
      menu: ['Agenda', 'Agendamentos'],
      text: 'Na agenda, cada card é um agendamento. Vamos abrir um, iniciar o atendimento e registrar o que foi feito: tudo fica no histórico do paciente.',
    },
    done: {
      title: 'Atendimento registrado!',
      text: 'Tudo o que você anotou ficou no histórico do paciente. Agora vamos registrar o pagamento desse atendimento.',
    },
    steps: [
      {
        target: 'agenda-appointment',
        title: 'Clique no agendamento',
        titleCompact: 'Toque no agendamento',
        text: 'Abra o agendamento do seu paciente. Se ele estiver em outra semana, use as setas do topo da agenda.',
        textCompact: 'Abra o agendamento da pessoa. Se ele estiver em outro dia, use as setas do topo da agenda.',
        dim: true,
      },
      {
        target: 'appointment-attend',
        title: 'Clique em Atender',
        text: 'Abre a tela do atendimento, com a ficha e o histórico do paciente.',
        dim: true,
      },
      {
        target: 'appointment-start',
        title: 'Clique em Iniciar',
        text: 'O cronômetro do atendimento começa a contar.',
        dim: true,
      },
      {
        target: 'appointment-type-confirm',
        title: 'Escolha o tipo de atendimento',
        text: 'Ele decide quais campos aparecem na tela. Depois clique em Iniciar atendimento.',
        dim: false,
      },
      {
        // Só aparece quando o tipo tem o módulo de queixa e evolução, que o Registrar exige
        target: 'appointment-entry',
        title: 'Escreva a queixa e a evolução',
        text: 'Este tipo de atendimento pede os dois para registrar: preencha a Queixa e a Evolução. Depois o guia mostra o Finalizar e registrar.',
        dim: false,
      },
      {
        target: 'appointment-finish',
        title: 'Agora clique em Finalizar e registrar',
        text: 'Se quiser, preencha os outros campos da tela antes. O botão para o tempo e abre o resumo para você conferir.',
        dim: false,
        ready: () => document.querySelector('[data-tour="appointment-finish"]')?.dataset.tourReady !== '0',
      },
      {
        target: 'appointment-register-confirm',
        title: 'Confira e clique em Registrar',
        text: 'Se faltar a queixa ou a evolução, o aviso mostra o que preencher.',
        dim: false,
        savedWhenGone: true,
      },
    ],
  },
  payment: {
    // Começa no "Faturar atendimento" da tela do atendimento; de outra tela, pela agenda
    path: '/schedule/appointments',
    pathLabel: 'Agenda',
    startHere: ['/appointment/'],
    intro: {
      title: 'Agora, o pagamento',
      menu: ['Agenda', 'Agendamentos'],
      text: 'Vamos faturar o atendimento que você registrou: você informa o valor e a forma de pagamento, e ele já entra no financeiro da clínica.',
    },
    done: {
      title: 'Pagamento registrado!',
      text: 'O valor já entrou no financeiro da clínica e o card da agenda mostra que está pago. Se precisar, a nota fiscal sai do mesmo lugar.',
    },
    steps: [
      {
        target: 'agenda-appointment',
        title: 'Clique no atendimento',
        titleCompact: 'Toque no atendimento',
        text: 'Abra o atendimento que você acabou de registrar. Se ele estiver em outra semana, use as setas do topo da agenda.',
        textCompact: 'Abra o atendimento que você acabou de registrar. Se ele estiver em outro dia, use as setas do topo da agenda.',
        dim: true,
      },
      {
        target: 'payment-invoice',
        title: 'Clique em Faturar atendimento',
        text: 'Fica na parte de Pagamento do agendamento.',
        dim: true,
      },
      {
        // Na tela do atendimento, logo depois de registrar
        target: 'appointment-invoice',
        title: 'Clique em Faturar atendimento',
        text: 'O atendimento que você acabou de registrar já pode ser faturado daqui mesmo.',
        dim: true,
      },
      {
        target: 'payment-save',
        title: 'Confira o valor e fature',
        text: 'O valor vem do atendimento. Escolha a forma de pagamento e clique no botão destacado para faturar.',
        dim: false,
        highlight: ['payment-price', 'payment-method'],
      },
    ],
  },
  statistics: {
    path: '/statistics',
    pathLabel: 'Estatísticas',
    intro: {
      title: 'Os números da clínica',
      menu: ['Análises', 'Estatísticas'],
      text: 'Com um agendamento, um atendimento e um pagamento, a clínica já tem números. Em Estatísticas você acompanha atendimentos, faturamento e pacientes do período, sem planilha.',
    },
    done: {
      title: 'Agora você conhece os números!',
      text: 'Cada atendimento e cada pagamento entram aqui sozinhos. Troque o período no topo para comparar meses.',
    },
    steps: [
      {
        target: 'statistics-overview',
        title: 'O resumo do período',
        text: 'Aqui ficam atendimentos, faturamento e pacientes. Mais abaixo, cada assunto tem a própria seção, e o período muda no topo da tela.',
        dim: true,
        confirm: 'Entendi',
      },
    ],
  },
};

export default TOUR_SCRIPTS;
